<template>
  <div class="charging-point-type-edit pa-10">

    <div class="d-flex justify-end">
      <audit-trail-button entity-type="Token" :entity-id="$route.params.id" />
    </div>



    <title-page icon="ico-status-charging" class="mb-0">
      {{ $t("rfid.edit-rfid") }}
    </title-page>

    <div class="mt-5">
      <headline>
        {{ $tc('rfid.rfid') }}
      </headline>
      <v-form class="form-grid" ref="form" @submit.prevent="save">
        <v-row justify="center">
          <v-col>
            <form-field
                :label=" $t('rfid.rfid-card-no')"
                v-model="form.uid"
                :rules="[commonRules.required]"
                :readonly=true
            />
          </v-col>
          <v-col>
            <form-field
                :label=" $t('rfid.rfid-card-name')"
                v-model="form.name"
            />
          </v-col>

        </v-row>
        <v-row justify="center">
          <v-col>
            <form-field
                :label="$t('rfid.rfid-card-physical-reference')"
                v-model="form.physicalReference"
            />
          </v-col>
          <v-col>
          </v-col>

        </v-row>

        <v-row>
          <v-col>
            <form-field
                :label=" $t('rfid.rfid-card-enabled')"
                v-model="form.enabled"
                type="switch"
            />
          </v-col>
          <v-col>
            <form-field
                :label=" $t('rfid.rfid-corporate')"
                v-model="form.corporate"
                type="switch"
            />
          </v-col>
          <v-col v-if="isSupervisor">
            <form-field
                :label="$t('rfid.rfid-roaming')"
                v-model="form.roaming"
                type="switch"
            />
          </v-col>
          <v-col v-if="currentUser.isZE">
            <form-field
                :label=" $t('rfid.third-party')"
                v-model="form.private"
                type="switch"
            />
          </v-col>
        </v-row>
        <!-- Désactiver un badge actif se justifie : la raison reste sur le badge et va au journal -->
        <v-row v-if="deactivating">
          <v-col cols="6">
            <v-select
                v-model="form.deactivationReason"
                :items="deactivationReasons"
                :label="$t('rfid.deactivation-reason')"
                :hint="$t('rfid.deactivation-reason-hint')"
                :rules="[commonRules.required]"
                persistent-hint outlined dense
            />
          </v-col>
          <v-col cols="6" v-if="form.deactivationReason === 'other'">
            <v-text-field
                v-model="form.deactivationNote"
                :label="$t('rfid.deactivation-note')"
                :rules="[commonRules.required]"
                outlined dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <form-field
                type="customer-select"
                :label="$tc('customer.customer',1)"
                v-model="form.customerId"
                selection-with-no-extra
                col-field="9"
                clearable
            />
          </v-col>
          <v-col>
            <form-field
                type="organization-select"
                :label="$tc('organization.organization', 1)"
                v-model="form.organizationId"
                selection-with-no-extra
                col-field="9"
                clearable
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="6">
            <vehicle-plate-combobox
                :label="$t('rfid.assigned-vehicle')"
                :value="vehiclePlateValue"
                :vehicles="vehicles"
                :disabled="!form.customerId || vehiclesLoading"
                :loading="vehiclesLoading"
                @change="onVehiclePlateChange"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col class="d-flex justify-center">
            <v-btn class="mr-5" @click="cancel">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn color="primary" type="submit">
              {{ $t('save') }}
            </v-btn>
          </v-col>

        </v-row>
      </v-form>
    </div>

  </div>
</template>

<script>
import { asksConsent, confirmProfessional, savedNotice } from '../professionalConsent'
import AuditTrailButton from "@elements/AuditTrailButton.vue";


import RulesMixin from "@mixins/RulesMixin";
import TokenRepository from "@repository/TokenRepository";
import VehicleRepository from "@repository/VehicleRepository";
import CustomerSelect from "@blocks/Select/CustomerSelect.vue";
import OrganizationSelect from "@blocks/Select/OrganizationSelect.vue";
import VehiclePlateCombobox from "@blocks/Select/VehiclePlateCombobox.vue";

export default {
  components: {AuditTrailButton,  VehiclePlateCombobox },


  mixins: [RulesMixin],
  data: () => ({
    form: {
      customerId: null,
      organizationId: null,
      vehicleId: null,
      // Free-text plate (unified mechanism). One of vehicleId / this is set.
      vehicleRegistrationNumber: null,
    },
    vehicles: [],
    vehiclesLoading: false,
  }),
  watch: {

    form: {
      handler(val, oldVal) {
        if(oldVal.id) {
          this.setUnsavedChanges()
        }
      },
      deep: true
    },

    'form.customerId'(newId, oldId) {
      if (newId === oldId) return
      // Customer changed → previous vehicle no longer applies
      if (oldId !== null) {
        this.form.vehicleId = null
        this.form.vehicleRegistrationNumber = null
      }
      this.loadVehicles(newId)
    },
  },
  mounted() {
    if (!this.currentUser.permissions.includes('OPERATOR_WRITE')) {
      this.$router.replace({name: 'dashboard'})
      return
    }
    this.form = this.cloneObj(this.token)
    this.form.customerId = this.token?.customer?.id ?? null
    this.form.organizationId = this.token?.organization?.id ?? null
    this.form.vehicleId = this.token?.vehicleId ?? this.token?.vehicle?.id ?? null
    this.loadVehicles(this.form.customerId)
  },
  computed: {
    token() {
      return this.$parent.token ?? null
    },

    /** Le badge était actif et ne l'est plus : on demande pourquoi. */
    deactivating() {
      return !!this.token?.enabled && !this.form.enabled
    },

    deactivationReasons() {
      return ['stolen', 'lost', 'unused', 'other'].map(value => ({ value, text: this.$t(`rfid.deactivation-reason-${value}`) }))
    },

    vehiclePlateValue() {
      return {
        vehicleId: this.form.vehicleId,
        registrationNumber: this.form.vehicleRegistrationNumber,
      }
    },
  },
  methods: {
    onVehiclePlateChange(payload) {
      this.form.vehicleId = payload.vehicleId ?? null
      this.form.vehicleRegistrationNumber = payload.registrationNumber ?? null
    },

    loadVehicles(customerId) {
      if (!customerId) {
        this.vehicles = []
        return
      }
      this.vehiclesLoading = true
      VehicleRepository.search({ customerId, limit: 9999 }, 'token-vehicle-list').then(result => {
        this.vehicles = (result.items || []).filter(v => !v.archived)
        this.vehiclesLoading = false
      }).catch(err => {
        this.vehiclesLoading = false
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          this.notifyError(err)
        }
      })
    },

    async save() {
      if (this.$refs.form.validate()) {
        // Badge personnel déjà à la personne coché pro : son accord sera demandé, on le dit avant.
        if (asksConsent(this.token, this.form) && !(await confirmProfessional(this, this.token))) return
        const notice = savedNotice(this, this.token, this.form)
        const form = this.cloneObj(this.form)
        delete form.bigendianValidated
        delete form.createdAt
        delete form.paymentAttachedToOrganization
        delete form.whitelist
        if(!this.isSupervisor) {
          delete form.roaming
        }
        if (!this.deactivating) {
          delete form.deactivationReason
          delete form.deactivationNote
        } else if (form.deactivationReason !== 'other') {
          form.deactivationNote = null
        }
        this.showLoading(true)
        TokenRepository.update(form).then(data => {
          this.$parent.loadData()
          this.cleanUnsavedChanges()
          this.$router.replace({name: 'tokens.detail'})
          if (notice) this.$dialog.notify.info(notice, { timeout: 8000 })
          else this.$dialog.notify.success(this.$t('update-success'))
          this.showLoading(false)
        }).catch(err => {
          this.notifyError(err)
          this.showLoading(false)
        })
      }
      //
    },
    cancel() {
      this.$router.push({name: 'tokens.detail'})
    },
  },
}
</script>

<style lang="scss">

</style>
